/**
 * Константы и конфигурация палитры для ModelerX6.
 */
import {
	AND_JUNCTION,
	FULL_ELEMENT_MAP,
	JUNCTION,
	NOTE,
	OR_JUNCTION,
	STRATEGY_ELEMENT_MAP,
	MOTIVATION_ELEMENT_MAP,
	BUSINESS_ELEMENT_MAP,
	APPLICATION_ELEMENT_MAP,
	TECHNOLOGY_ELEMENT_MAP,
	PHYSICAL_ELEMENT_MAP,
	IMP_MIG_ELEMENT_MAP,
	OTHER_ELEMENT_MAP,
} from '../util/ModelUtil';
import {ARCHIMATE_GROUP, OTHER_GROUPING} from '../metamodel/Concept';
import {STICKER, STICKER_SHAPE_ID, STICKER_DEFAULT_SIZE} from '../util/CanvasNodeTypes';

export const NAMESPACE_URI = 'http://www.opengroup.org/xsd/archimate/3.0/';

export const TEMPLATE_MODEL = `<?xml version="1.0" encoding="UTF-8"?>
<archimate:Model xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:archimate="${NAMESPACE_URI}" xsi:schemaLocation="http://www.opengroup.org/xsd/archimate/3.0/ http://www.opengroup.org/xsd/archimate/3.1/archimate3_Diagram.xsd">
    <name>New model</name>
    <documentation></documentation>
    <archimate:Elements>
    </archimate:Elements>
    <archimate:Views>
        <archimate:Diagrams>
            <archimate:View>
                <name>Диаграмма 1</name>
                <documentation></documentation>
            </archimate:View>
        </archimate:Diagrams>
    </archimate:Views>
</archimate:Model>`;

/** Минимальные размеры элемента при ресайзе (совпадают с Transform). При создании из палитры узел получает этот размер. */
export const MIN_ELEMENT_WIDTH = 150;
export const MIN_ELEMENT_HEIGHT = 50;

/** Group / Grouping: язычок + область тела — выше общего минимума. */
export const MIN_GROUP_ELEMENT_HEIGHT = MIN_ELEMENT_HEIGHT + 30;

/** X6 shape id для групп с язычком (getMinElementHeightForShape). */
export const GROUP_LIKE_SHAPE_IDS = Object.freeze(['archimate:group', 'archimate:grouping']);

/**
 * @param {string} [shapeId]
 * @returns {number}
 */
export function getMinElementHeightForShape(shapeId) {
	if (shapeId === STICKER_SHAPE_ID) return STICKER_DEFAULT_SIZE.height;
	return GROUP_LIKE_SHAPE_IDS.includes(shapeId) ? MIN_GROUP_ELEMENT_HEIGHT : MIN_ELEMENT_HEIGHT;
}

/**
 * Параметры X6 router `manhattan`.
 *
 * Предупреждение `Unable to execute manhattan algorithm, use orth instead` выводится в X6
 * (`registry/router/manhattan/router.js`), когда `findRoute` возвращает `null`: чаще всего все кандидатные точки выхода с границы
 * source/target попадают внутрь «препятствий» (bbox других узлов, расширенных на padding),
 * либо A* не успевает за `maxLoopCount`.
 *
 * По умолчанию у X6 padding ≈ step (10px) с каждой стороны — на плотных ArchiMate-диаграммах
 * это даёт частый fallback на orth. Уменьшаем padding и поднимаем лимит итераций.
 */
export const X6_MANHATTAN_ROUTER_ARGS = Object.freeze({
	padding: 4,
	maxLoopCount: 15000,
});

/**
 * Якорь мини-иконки picto (правый верх угла тела узла): фиксированные отступы от границы,
 * как у rect-элементов ArchiMate — не прижимать к краю при refX: 100%.
 */
export const ARCHIMATE_PICTO_ICON_CORNER = Object.freeze({
	refX: '100%',
	refX2: -30,
	refY: 0,
	refY2: 8,
});

/**
 * Опция `connecting.router` для Graph и объект `router` при создании Edge.
 *
 * Режим «Произвольный» (`routerName === 'normal'` или устаревший `'orth'`) → X6 **`normal`**:
 * router **`orth`** при каждом изменении вершин заново вычисляет ортогональный путь между
 * якорями и bendpoint’ами — при перетаскивании сегментов/вершин это даёт хаотичную
 * перестройку. **`normal`** оставляет заданные вершины как есть; ортогональный вид
 * по-прежнему дают `anchor: 'orth'`, `connectionPoint: 'boundary'` и connector `jumpover`
 * (как при отсутствии явного router в старых версиях).
 *
 * `manhattan` — отдельный режим; там vertices/segments tools отключены в cellClickPlugin.
 *
 * @param {'normal'|'orth'|'manhattan'} routerName — orth синоним normal (обратная совместимость)
 * @returns {{ name: 'normal' } | { name: 'manhattan', args: Record<string, unknown> }}
 */
export function buildX6RouterConfig(routerName) {
	if (routerName === 'manhattan') {
		return {name: 'manhattan', args: {...X6_MANHATTAN_ROUTER_ARGS}};
	}
	return {name: 'normal'};
}

export const STENCIL_GROUPS = [
	{name: 'strategy', title: 'Strategy', map: STRATEGY_ELEMENT_MAP},
	{name: 'motivation', title: 'Motivation', map: MOTIVATION_ELEMENT_MAP},
	{name: 'business', title: 'Business', map: BUSINESS_ELEMENT_MAP},
	{name: 'application', title: 'Application', map: APPLICATION_ELEMENT_MAP},
	{name: 'technology', title: 'Technology', map: TECHNOLOGY_ELEMENT_MAP},
	{name: 'physical', title: 'Physical', map: PHYSICAL_ELEMENT_MAP},
	{name: 'imp-mig', title: 'Implementation & Migration', map: IMP_MIG_ELEMENT_MAP},
	{name: 'other', title: 'Other', map: OTHER_ELEMENT_MAP},
];

/** Формы с path-based body (hexagon, grouping, group, note): при ресайзе пересчитывается path. */
export const PATH_BASED_SHAPES = [
	'archimate:element-hexagon',
	'archimate:grouping',
	'archimate:group',
	'archimate:label',
];

export {
	NOTE,
	JUNCTION,
	AND_JUNCTION,
	OR_JUNCTION,
	ARCHIMATE_GROUP,
	OTHER_GROUPING,
	FULL_ELEMENT_MAP,
	STICKER,
	STICKER_SHAPE_ID,
	STICKER_DEFAULT_SIZE,
};
